// Copy rules: labels and headings render uppercase, no em dashes, illustrative numbers are labeled SAMPLE.
import { Language, useLanguage } from '../../context/LanguageContext';
import es from './content.es';
import pt from './content.pt';

export const CONSULT_URL = 'https://calendly.com/alioit/group-meet';
export const ASSESSMENT_URL = 'https://calendly.com/alioit/30min';

export type TermLine = { kind: 'cmd' | 'out' | 'ok'; text: string };
export type Card = { tag: string; title: string; body: string; href: string; variant?: 'primary' | 'more'; stat?: string };
export type NavItem = { label: string; href: string };

const terminalLines: TermLine[] = [
  { kind: 'cmd', text: 'alio engage --mode=forward-deployed --industry=healthcare' },
  { kind: 'out', text: '→ embedding 2 senior engineers · US + MX overlap hours' },
  { kind: 'out', text: '→ mapping workflows: intake, claims review, reporting' },
  { kind: 'cmd', text: 'alio ship rag-assistant --env=prod' },
  { kind: 'out', text: '→ pgvector index built · 48,000 documents' },
  { kind: 'out', text: '→ evals 212/212 passing · human review gate: ON' },
  { kind: 'out', text: '→ HIPAA controls mapped in Drata' },
  { kind: 'ok', text: '✓ live in production · runbook handed to client team' },
  { kind: 'cmd', text: 'alio managedops --plan=growth' },
  { kind: 'out', text: '→ monitoring, FinOps, QA regression: scheduled' },
];

export const sharedTerminalLines = terminalLines;

export const clientNames = [
  'NinjaOne', 'Kimley-Horn', 'Olaplex', 'Boostlingo', 'Scout', 'Secberus', 'Tech To The Rescue',
  'USAHS', 'Cambium Learning', 'Focus Insurance', 'Time4Learning', 'LAZ Parking', 'AEO Inc.', 'TRICO',
];

export const stackItems = [
  'AWS', 'AZURE', 'GCP', 'OPENAI', 'AZURE OPENAI', 'PGVECTOR', 'SNOWFLAKE',
  'SALESFORCE', 'KUBERNETES', 'TERRAFORM', 'REACT', 'PYTHON', 'JAVA', '.NET',
  'HL7 FHIR', 'DRATA', 'PURVIEW', 'PLAYWRIGHT', 'KATALON', 'GITLAB',
];

export const offices = {
  usa: ['(520) 261-1117', '9040 S Rita Rd #1270, Tucson, AZ 85747', '7670 Opportunity Rd #255, San Diego, CA 92111'],
  mexico: ['Garmendia #202-I, Col. Centro', 'Hermosillo, Son. 83000'],
  india: ['i-Thum Tower-A', 'Sector 62, Noida 201309'],
};

const en = {
  langLabel: 'Language',
  menuLabel: 'Toggle menu',
  nav: [
    { label: '//PROOF', href: '/#proof' },
    { label: '//PROCESS', href: '/#process' },
    { label: '//SERVICES', href: '/#services' },
    { label: '//WORK', href: '/#work' },
    { label: '//MANAGEDOPS', href: '/#managedops' },
    { label: '//FAQ', href: '/#faq' },
  ] as NavItem[],
  navExtra: [
    { label: '//BLOG', href: '/blog' },
    { label: '//CAREERS', href: '/careers-opportunity' },
  ] as NavItem[],
  navStatus: 'ACCEPTING ENGAGEMENTS',
  navContact: 'CONTACT',
  likeSite: {
    prompt: 'LIKE THIS SITE?',
    thanks: 'THANKS',
  },

  hero: {
    eyebrow: '//FORWARD-DEPLOYED AI ENGINEERING',
    titleA: 'AI THAT ACTUALLY',
    titleB: 'SHIPS',
    sub: 'Alio embeds senior engineers inside your team to ship AI agents, RAG and automations into production, then keeps them running. Built for regulated, operationally complex organizations.',
    subStrong: 'Secure. Governed. Owned by your team.',
    rotorTag: '//WHAT THE TEAM SHIPS',
    rotor: [
      ['AI AGENTS', 'INTO PRODUCTION'],
      ['RAG', 'INTO YOUR STACK'],
      ['COMPLIANCE', 'INTO THE PIPELINE'],
      ['QA', 'INTO EVERY RELEASE'],
      ['CLOUD SPEND', 'DOWN 30-40%'],
    ] as [string, string][],
    rotorMeta: 'PROJECT DELIVERY OR MANAGEDOPS RETAINER',
    ctaPrimary: 'BOOK A CONSULT',
    ctaGhost: 'SEE THE WORK',
    meta: ['SINCE 2011', 'US · MEXICO · INDIA', 'HUMANS IN THE LOOP', 'PRODUCTION, NOT DEMOS'],
  },

  terminal: {
    title: 'alio engagement.log',
    chip: 'SAMPLE SESSION',
    aria: 'Sample Alio engagement session',
  },

  proof: {
    title: 'PROOF',
    chip: 'FROM CLIENT WORK',
    sub: 'Numbers from shipped engagements and published case studies. No pilots, no projections.',
    mainLabel: 'ACAPS · HUMANITARIAN AI PLATFORM',
    mainLabelDim: 'agents + pgvector RAG',
    big: [
      { to: 70, suffix: '%', cap: 'LESS ANALYST TIME', tone: 'accent' },
      { to: 60, suffix: '%', cap: 'LOWER SEARCH INFRA COST', tone: 'plain' },
    ],
    bars: [
      { name: 'NinjaOne tests automated', value: 60, label: '~60%' },
      { name: 'Faster compliance (Drata)', value: 60, label: '60%' },
      { name: 'Faster time to market', value: 40, label: '40%' },
      { name: 'Cloud spend cut (FinOps)', value: 40, label: '30-40%' },
      { name: 'Cloud migration savings', value: 35, label: '35%' },
    ],
    log: [
      { text: '· acaps: replace OpenSearch with pgvector, multilingual RAG', stat: '-60%', href: '/white-papers' },
      { text: '· ninjaone: automate ~1,000-test regression program, TDD', stat: '~60%', href: '/case-studies/ninjaone' },
      { text: '· usahs: Salesforce + Jenzabar + Core clinical data sync', stat: 'LIVE', href: '/case-studies/usahs' },
    ],
    sideA: {
      chip: '⌘ FOOTPRINT',
      label: 'since 2011',
      to: 3,
      suffix: '',
      cap: 'COUNTRIES · ONE TEAM',
      rows: ['ARIZONA, US', 'HERMOSILLO, MX', 'NOIDA, IN'],
    },
    sideB: {
      chip: '⌘ RANGE',
      label: 'industries shipped',
      to: 14,
      suffix: '+',
      cap: 'REGULATED + OPERATIONAL',
      rows: ['HEALTHCARE · FINTECH · INSURANCE', 'EDTECH · UTILITIES · MANUFACTURING'],
    },
  },

  clients: {
    title: 'TRUSTED BY',
    chip: '14 TEAMS ON THE WALL',
    partnersLabel: 'partners',
    partners: 'AWS · MICROSOFT · SALESFORCE · OPENAI · DRATA · GITLAB · MONGODB COE · GOOGLE CLOUD · BRAINCODERS.IO · ARIZONA TECHNOLOGY COUNCIL',
  },

  process: {
    title: 'PROCESS',
    chip: '5 STAGES · 1 TEAM',
    sub: 'One broken workflow in. One production system your team owns, out. Every stage hands evidence to the next.',
    steps: [
      { num: '01', tag: '//DISCOVER', title: 'INTENT → WORKFLOW MAP', body: 'We sit with your operators, analysts and leadership to find where work actually breaks. The map comes before the model.' },
      { num: '02', tag: '//EMBED', title: 'MAP → TEAM', body: 'Senior engineers from the US, Mexico and India join your standups, your repos and your channels. Same hours, same goals.' },
      { num: '03', tag: '//BUILD', title: 'TEAM → SYSTEMS', body: 'Agents, RAG pipelines, integrations and cloud infrastructure, shipped in sprints against acceptance criteria you sign off.' },
      { num: '04', tag: '//VERIFY', title: 'SYSTEMS → EVIDENCE', body: 'Automated QA, evaluations, security review and compliance mapping. Human review gates wherever the risk is real.' },
      { num: '05', tag: '//OPERATE', title: 'EVIDENCE → OWNERSHIP', body: 'Runbooks and knowledge transfer so your team owns it. Or keep us on through ManagedOps and we keep improving it.' },
    ],
    flow: ['discover', 'embed', 'build', 'verify', 'operate'],
    flowEnd: 'loop until it works in your environment',
  },

  services: {
    title: 'SERVICES',
    chip: 'AI FIRST · FULL STACK',
    sub: 'Not one generalist vendor. Specialist teams for AI, cloud, security and delivery, under one engagement.',
    cards: [
      { tag: '//PRIMARY', title: 'Forward-deployed AI engineering', body: 'Senior engineers embed with your operators, map the broken workflows and ship agents, RAG and automations in production. Not shelf-ware demos.', href: '/solutions/ai-engineering', variant: 'primary' },
      { tag: '//OPERATIONS', title: 'Alio ManagedOps', body: 'Managed engineering operations, not a help desk. Cloud, DevOps, QA, security and AI, continuously improved on a monthly retainer.', href: '/managedops' },
      { tag: '//CLOUD', title: 'Cloud & DevOps', body: 'AWS, Azure and GCP. CI/CD, infrastructure as code, observability and FinOps that cuts 30-40% of cloud spend.', href: '/services' },
      { tag: '//SECURITY', title: 'Cybersecurity & vCISO', body: 'Security leadership without the full-time hire. Risk programs, audit prep, incident planning and vendor review.', href: 'https://vciso.alioit.com' },
      { tag: '//HEALTHCARE', title: 'HealthTech & interoperability', body: 'HL7 FHIR, SMART on FHIR and USCDI mapping, with HIPAA, SOC 2 and HITRUST readiness built in.', href: 'https://healthtech.alioit.com' },
      { tag: '//QA', title: 'QA automation', body: 'Selenium, Cypress, Playwright and Katalon. AI-assisted test generation with human QA review. Nothing ships on a prayer.', href: '/case-studies/ai-qa-automation' },
      { tag: '//CRM', title: 'Salesforce', body: 'Sales, Service and Marketing Cloud. Certified admins and developers for builds, integrations and data migration.', href: '/case-studies/salesforce' },
      { tag: '//BUILD', title: 'Software development', body: 'Web, mobile and cloud apps across React, Node.js, Java, Python, Go and .NET. Product-minded, release-ready.', href: '/services' },
      { tag: '//MORE', title: '+5 more', body: 'UX/UI · data engineering · Web3 · manufacturing PLM · recruitment →', href: '/services', variant: 'more' },
    ] as Card[],
  },

  stack: {
    title: 'STACK',
    chip: 'YOUR ENVIRONMENT, NOT OURS',
    sub: 'We learn your platform instead of selling you a new one. If it runs your business, we can ship on it.',
    last: '+ YOUR STACK',
    note: '// integrate with systems of record, not around them',
  },

  work: {
    title: 'WORK',
    chip: 'CASE STUDIES',
    sub: 'Shipped and running. Click through for the architecture, the stack and the outcome.',
    cards: [
      { tag: '//HUMANITARIAN AI', title: 'ACAPS', body: 'AI knowledge platform for humanitarian intelligence. Agents, pgvector RAG and multilingual LLM processing.', stat: '-70% ANALYST TIME', href: '/white-papers' },
      { tag: '//MSP · B2B SAAS', title: 'NinjaOne', body: 'Remote QA partnership automating a ~1,000-test program and advancing test-driven development.', stat: '~60% AUTOMATED', href: '/case-studies/ninjaone' },
      { tag: '//HIGHER ED', title: 'USAHS', body: 'Clinical placement and student data integration across Salesforce, Jenzabar and Core. Less manual reconciliation.', stat: '3 SYSTEMS IN SYNC', href: '/case-studies/usahs' },
      { tag: '//EDTECH', title: 'Time4Learning', body: 'PreK-12 learning platform. Application support, web and mobile development, UX/UI. Ongoing since 2022.', stat: 'SINCE 2022', href: '/case-studies/time4learning' },
      { tag: '//FINTECH', title: 'Nerito', body: 'Payments platform for Mexico\'s unbanked. Mobile payments, deposits and withdrawals on serverless AWS.', stat: 'SERVERLESS', href: '/case-studies/nerito' },
      { tag: '//AI QA', title: 'AI-augmented QA', body: 'AI-first QA model with Katalon, Jira, QMetry and Copilot CLI. Faster scripting, faster failure triage.', stat: 'AI + HUMAN REVIEW', href: '/case-studies/ai-qa-automation' },
    ] as Card[],
  },

  managedops: {
    title: 'MANAGEDOPS',
    chip: 'SECURE · STABLE · OPTIMIZED',
    sub: 'Launch is the start, not the finish. ManagedOps keeps your cloud, security, QA and AI improving every month on an outcome-based retainer.',
    plansLabel: '//MONTHLY PLANS',
    plansDim: 'scoped after assessment',
    colPlan: 'PLAN',
    colScope: 'SCOPE',
    colPrice: 'RETAINER',
    plans: [
      { name: 'ESSENTIALS', desc: 'Monthly ops review, ticket support, cloud, security and QA backlog', price: '$3.5K-$7K/MO' },
      { name: 'GROWTH', desc: 'CI/CD support, FinOps, QA maintenance, security remediation, exec reviews', price: '$7.5K-$14K/MO' },
      { name: 'ENTERPRISE', desc: 'End-to-end cloud, AI, QA, security and compliance ops, dedicated leadership', price: '$15K-$30K+/MO' },
    ],
    plansFoot: '// final pricing depends on platform scope, support hours, compliance needs and response SLAs',
    vsLabel: '⌘ TRADITIONAL MSP → ALIO MANAGEDOPS',
    vs: [
      ['Help desk support', 'Engineering operations'],
      ['Device management', 'Cloud, DevOps, QA, AI, security'],
      ['Reactive tickets', 'Continuous improvement'],
      ['Per-user pricing', 'Outcome-based retainers'],
    ] as [string, string][],
    vsFoot: '// full breakdown at',
    areas: [
      { title: 'MANAGED CLOUD & DEVOPS', body: 'AWS, Azure, GCP. CI/CD monitoring, IaC upkeep, FinOps, observability and production support.' },
      { title: 'CYBERSECURITY & vCISO', body: 'Advisory, audit prep, incident response planning, risk remediation and vendor risk review.' },
      { title: 'HEALTHTECH SECURITY', body: 'HIPAA risk assessments, SOC 2 and HITRUST readiness, PHI protection and security training.' },
      { title: 'QA AUTOMATION', body: 'Regression maintenance, release readiness and failure triage. AI-generated tests, human-reviewed.' },
      { title: 'AI AGENT DEVELOPMENT', body: 'Domain agents, tools and RAG pipelines with evaluations, safety checks and human-in-the-loop workflows.' },
      { title: 'AI OPERATIONS', body: 'Model, prompt and RAG upkeep, AI governance, and cost and performance monitoring across vendors.' },
    ],
    cta: 'BOOK A MANAGEDOPS ASSESSMENT',
  },

  principles: {
    title: 'PRINCIPLES',
    chip: 'HOW WE WORK',
    rows: [
      { num: '01', title: 'PRODUCTION, NOT DEMOS', body: 'If it isn\'t running in your environment, it isn\'t done. A pilot is a step, never the deliverable.' },
      { num: '02', title: 'HUMANS IN THE LOOP', body: 'AI augments your team; it never replaces judgment. Review gates sit wherever the risk is real.' },
      { num: '03', title: 'COMPLIANCE BY DEFAULT', body: 'HIPAA, SOC 2, ISO 27001 and GDPR controls get mapped while we build... not bolted on the week before the audit.' },
      { num: '04', title: 'YOUR TEAM OWNS IT', body: 'Documentation, runbooks and mentoring on every engagement. We design for the handoff from day one.' },
    ],
  },

  faq: {
    title: 'FAQ',
    chip: '[ + ] EXPANDS',
    items: [
      { q: 'What is forward-deployed AI engineering?', a: 'Senior Alio engineers embed inside your organization to discover real workflows, then build and ship AI agents, RAG systems and automations in your production environment, with human-in-the-loop governance and knowledge transfer so your team keeps ownership. Available as project delivery or as an ongoing ManagedOps service.' },
      { q: 'How is this different from staff augmentation or an MSP?', a: 'Staff augmentation hands you headcount. An MSP hands you a help desk. Alio hands you outcomes: senior engineers who own discovery, delivery and operations, on project terms or an outcome-based ManagedOps retainer.' },
      { q: 'Which industries do you work with?', a: 'Healthcare, FinTech, insurance, EdTech and higher education, SaaS and MSP platforms, manufacturing, mining, logistics, utilities, electric coops, streaming, cybersecurity and humanitarian non-profits.' },
      { q: 'How do you handle security and compliance?', a: 'vCISO advisory, HealthTech security, Microsoft Purview and AWS security integration, and Drata-backed compliance automation for SOC 2, ISO 27001, HIPAA and GDPR, which runs about 60% faster than a manual program.' },
      { q: 'Can you work alongside our internal team?', a: 'Yes. We plug into your Scrum ceremonies for AI engineering, DevOps, QA automation, frontend, data pipelines and UX, or run ongoing operations through a ManagedOps retainer.' },
      { q: 'How does an engagement start?', a: 'With a call. Book a general consult or a 30-minute ManagedOps assessment. We scope the work, match the engineers and start with discovery.' },
    ],
  },

  cta: {
    eyebrow: '//ENGAGE',
    titleA: 'STOP PILOTING',
    titleB: 'START SHIPPING',
    sub: 'Bring us the workflow that keeps breaking. We\'ll bring the team that ships the fix.',
    primary: 'BOOK A CONSULT',
    ghost: 'MANAGEDOPS ASSESSMENT',
  },

  contact: {
    title: 'CONTACT',
    chip: 'US · MX · IN',
    sub: 'Tell us what keeps breaking and where it runs. We\'ll come back with a plan, not a pitch.',
    officesLabel: '//OFFICES',
    regions: { usa: 'USA', mexico: 'MEXICO', india: 'INDIA' },
    formLabel: '//PROJECT BRIEF',
    formDim: 'new_engagement.form',
    required: '* required',
    sending: 'SENDING...',
    orBook: '// rather talk now?',
    orBookLink: 'book a consult →',
  },

  footer: {
    tag: 'YOUR REMOTE TEAM, BUT CLOSER.',
    status: 'US · MX · IN ONLINE',
    cols: [
      { head: '//SERVICES', links: [
        { label: 'AI engineering', href: '/solutions/ai-engineering' },
        { label: 'ManagedOps', href: '/managedops' },
        { label: 'All services', href: '/services' },
        { label: 'vCISO →', href: 'https://vciso.alioit.com' },
      ] },
      { head: '//COMPANY', links: [
        { label: 'Company', href: '/company' },
        { label: 'Case studies', href: '/case-studies' },
        { label: 'Careers', href: '/careers-opportunity' },
        { label: 'Community', href: '/community' },
      ] },
      { head: '//RESOURCES', links: [
        { label: 'Blog', href: '/blog' },
        { label: 'White papers', href: '/white-papers' },
        { label: 'UI/UX portfolio', href: '/uiux-portfolio' },
        { label: 'Privacy', href: '/privacy' },
      ] },
    ],
    legalA: '© 2026 ALIO IT SOLUTIONS LLC · ARIZONA · HERMOSILLO · NOIDA',
    legalB: '+1 (520) 261-1117',
  },
};

export type Content = typeof en;

const byLanguage: Record<Language, Content> = { en, es, pt };

export const useContent = (): Content => {
  const { language } = useLanguage();
  return byLanguage[language] ?? en;
};

export default en;
